import React, { useState, useRef, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { selectFilters } from '../../reducers/filtersReducer';
import Header from '../Header/Header';
import useFilterOptions from '../../hooks/useFilterOptions';
import { selectZipCode } from '../../reducers/zipCodeReducer';
import { StickyNavHeight, getNavConfigs } from '../../constants/navConfigs';
import FeaturedOffersList from '../FeaturedOffers/FeaturedOffersList';
import { device } from '../../styles/devices';
import OffersListContainer from '../OffersListContainer';
import { groupOffersByModelGroup, groupModelFamily } from '../../utils/groupOffers';
import { sortVehicleByModel } from '../../utils/sortVehiclesByModel';
import { SORTING_CONFIG } from '../../constants/ModelFamilyList';
import getRandomFeaturedOffers from '../../utils/getRandomFeaturedOffers';
import sortFeaturedOffers from '../../utils/sortFeaturedOffers';
import {
  FeaturedOfferSection,
  OffersListSection,
  OffersListWrapper,
  SkipToSection,
  GeneralSidePadding,
} from './OffersList.styles';
import SkipTo from '../SkipTo';
import FilterTags from '../FilterResults/FilterTags';
import ResultsCount from '../ResultsCount';
import { selectOffers } from '../../reducers/offersReducer';
import NoOffers from '../NoOffers';
import { USOffersWrapper } from '../Offers.styles';
import FilterDrawer from '../Filters/FilterDrawer';
import useScrollTracker, {
  useMainComponentScrollTracker,
  useScrollVerticalTracker,
} from '../../hooks/useScrollTracker';
import ShowFilterCTA from '../ShowFilterCTA';
import SkeletonLoader from '../Skeleton';

type MainPageProps = {
  loading: boolean;
};

const OffersList: React.FC<MainPageProps> = ({ loading }) => {
  const isLaptop = useMediaQuery({ query: device.laptop });
  const isStickyLaptop = useMediaQuery({ query: device.laptop });
  const isBigMobile = useMediaQuery({ query: '(min-width: 480px)' });
  const isResolutionForSticky = isBigMobile && !isStickyLaptop;

  const contentRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const listRef = useRef(null);

  const { confirmedZipcode } = useSelector(selectZipCode);
  const { confirmedFilters } = useSelector(selectFilters);
  const { offersList } = useSelector(selectOffers);

  const [notScroll, setNotScroll] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(!!isLaptop);

  const isNatOffers = confirmedZipcode === '';

  // For scrolling and animation
  // ----------------------------------------------------------------------------
  const distanceFromTopToMainComponent = useMainComponentScrollTracker(listRef);
  const isReachedEndOfContainer = useScrollVerticalTracker(listRef);
  const mobileScrolled = useScrollTracker(368);
  const scrolled = useScrollTracker(distanceFromTopToMainComponent - 28);
  // ----------------------------------------------------------------------------

  // Filtering and splitting offers logic
  // ----------------------------------------------------------------------------
  const { modelFamilyOptions } = useFilterOptions();

  const groupedModelFamilies = useMemo(() => {
    return groupModelFamily(modelFamilyOptions);
  }, [modelFamilyOptions]);

  const getSelectedFiltersCount = useMemo(() => {
    return Object.values(confirmedFilters).reduce(
      (total, filterArray) => total + filterArray.length,
      0,
    );
  }, [confirmedFilters]);

  const isAnyFilterSelected = useMemo(() => {
    return getSelectedFiltersCount > 0;
  }, [getSelectedFiltersCount]);

  const filteredOffers = useMemo(() => {
    return offersList.filter((offer) => {
      return (
        (!confirmedFilters.offerType.length ||
          confirmedFilters.offerType.includes(offer.offerType.toLowerCase())) &&
        (!confirmedFilters.bodyType.length ||
          confirmedFilters.bodyType.includes(offer?.offerModelDetail?.bodyType.toLowerCase())) &&
        (!confirmedFilters.modelFamily.length ||
          confirmedFilters.modelFamily.includes(
            offer.offerModelDetail.modelFamily.replace(/[ -]/g, '').toLowerCase(),
          )) &&
        (!confirmedFilters.trimLine.length ||
          confirmedFilters.trimLine.includes(
            offer?.offerModelDetail?.trimline.toLowerCase().replace(' ', '_'),
          )) &&
        (!confirmedFilters.modelYear.length ||
          confirmedFilters.modelYear.includes(offer?.offerModelDetail?.modelYear.toString()))
      );
    });
  }, [
    confirmedFilters.bodyType,
    confirmedFilters.modelFamily,
    confirmedFilters.modelYear,
    confirmedFilters.offerType,
    confirmedFilters.trimLine,
    offersList,
  ]);

  const sortedOffers = useMemo(() => {
    const groupedOffers = groupOffersByModelGroup(filteredOffers, groupedModelFamilies);
    const sortedModels = sortVehicleByModel(groupedOffers, SORTING_CONFIG);
    return sortedModels;
  }, [filteredOffers, groupedModelFamilies]);

  const featuredOffers = useMemo(() => {
    if (isAnyFilterSelected) return [];
    const sortedFeaturedOffers = sortFeaturedOffers(offersList);
    const featuredOffersList = sortedFeaturedOffers.filter((offer) => offer.programPriority === 1);
    const featuredOffersCount = featuredOffersList.length;
    if (featuredOffersCount < 4) {
      const randomFeaturedOffers = getRandomFeaturedOffers(sortedFeaturedOffers);
      return randomFeaturedOffers;
    }
    return featuredOffersList?.slice(0, 5);
  }, [isAnyFilterSelected, offersList]);

  // ----------------------------------------------------------------------------

  // Code realted to smooth scrolling in Skip To section
  // ----------------------------------------------------------------------------
  const navConfigs = getNavConfigs(
    isAnyFilterSelected,
    confirmedFilters,
    groupedModelFamilies,
    sortedOffers,
  );

  const handleScrollFlag = (flag: boolean) => {
    setNotScroll(flag);
  };

  const handleOpenFilters = useCallback(() => {
    setIsFiltersOpen(true);
  }, []);

  const executeScroll = useCallback(
    (goTo: string) => {
      const ref = contentRefs.current[goTo];
      if (ref) {
        ref.style.scrollMargin = StickyNavHeight(isLaptop).strValue;
        ref.scrollIntoView({ behavior: 'smooth' });
      }
    },
    [isLaptop],
  );

  const setRef = (key: string) => (el: HTMLDivElement | null) => {
    contentRefs.current[key] = el;
  };
  // ----------------------------------------------------------------------------

  const RenderShowFiltersSticky = useMemo(() => {
    // Complex logic to show Show Filters
    // Case 1: laptop and filters are not open
    // Case 2, less than laptop (1024px), but more than 480px, scrolled till regular show filter dissapear
    // and filter are not opened
    if (
      (!isFiltersOpen && isStickyLaptop) ||
      (mobileScrolled && !isFiltersOpen && isResolutionForSticky)
    )
      return (
        <ShowFilterCTA
          isFiltersOpen={isFiltersOpen}
          setIsFiltersOpen={setIsFiltersOpen}
          scrolled={scrolled}
        />
      );
    return null;
  }, [isFiltersOpen, isStickyLaptop, mobileScrolled, scrolled, isResolutionForSticky]);

  const RenderOffers = useMemo(() => {
    if ((!offersList && loading) || loading) return <SkeletonLoader />;
    if ((!offersList && !loading) || !offersList?.length) return <NoOffers />;
    return (
      <OffersListWrapper>
        <GeneralSidePadding isFiltersOpen={isFiltersOpen}>
          <Header
            isNational={isNatOffers}
            dag={filteredOffers[0]?.zipClusterID}
            isFiltersOpen={isFiltersOpen}
          />
        </GeneralSidePadding>
        <GeneralSidePadding isFiltersOpen={isFiltersOpen}>
          <SkipToSection>
            <SkipTo
              notScrollFlag={notScroll}
              executeScroll={executeScroll}
              navConfigs={navConfigs}
              handleScrollFlag={handleScrollFlag}
              handleOpenFilters={handleOpenFilters}
              isFiltersOpen={isFiltersOpen}
              selectedFiltersCount={getSelectedFiltersCount}
            />
          </SkipToSection>
        </GeneralSidePadding>
        <GeneralSidePadding isFiltersOpen={isFiltersOpen}>
          <FilterTags />
        </GeneralSidePadding>
        {isAnyFilterSelected && (
          <GeneralSidePadding isFiltersOpen={isFiltersOpen}>
            <ResultsCount totalCount={filteredOffers.length} />
          </GeneralSidePadding>
        )}
        <FeaturedOfferSection>
          <FeaturedOffersList featuredOffers={featuredOffers} />
        </FeaturedOfferSection>
        <GeneralSidePadding isFiltersOpen={isFiltersOpen}>
          <OffersListSection>
            <OffersListContainer
              handleSectionRef={setRef}
              offers={sortedOffers}
              groupedModels={groupedModelFamilies}
              isAnyFilterSelected={isAnyFilterSelected}
              modelFamilyOptions={modelFamilyOptions}
              isFiltersOpen={isFiltersOpen}
            />
          </OffersListSection>
        </GeneralSidePadding>
      </OffersListWrapper>
    );
  }, [
    executeScroll,
    featuredOffers,
    filteredOffers,
    getSelectedFiltersCount,
    groupedModelFamilies,
    handleOpenFilters,
    isAnyFilterSelected,
    isFiltersOpen,
    isNatOffers,
    loading,
    modelFamilyOptions,
    navConfigs,
    notScroll,
    offersList,
    sortedOffers,
  ]);

  return (
    <USOffersWrapper ref={listRef}>
      <FilterDrawer
        isFiltersOpen={isFiltersOpen}
        setIsFiltersOpen={setIsFiltersOpen}
        isReachedEndOfContainer={isReachedEndOfContainer}
      />
      {RenderShowFiltersSticky}
      {RenderOffers}
    </USOffersWrapper>
  );
};

export default OffersList;
